import type { Vec3 } from '../../geo/types';

export type ShipType = 'kraeck' | 'hulk' | 'bark' | 'boeier' | 'rowing' | 'praam';

/** Differences between working boats, independent of their craft class and rig. */
export interface BoatAppearance {
  readonly lengthScale: number;
  readonly beamScale: number;
  readonly finish: 'tarred' | 'weathered' | 'oak';
}
/**
 * Which of her sails a vessel spreads: every one, none, the course of her foremost square mast
 * alone (R3, R34), or that course with her lateen mizzen (R16).
 */
export type SailState = 'furled' | 'set' | 'fore' | 'foreAndMizzen';

export interface ShipTemplate {
  readonly type: ShipType;
  readonly length: number;
  readonly beam: number;
  readonly masts: readonly MastConfig[];
  /** How far the sheer rises at the stem and at the stern: of the gunwale, or of a formed hull's waist rail. */
  readonly sheerFore: number;
  readonly sheerAft: number;
  readonly keelDepth: number;
  readonly hullExponent: number;
  readonly hasLeeboards: boolean;
  /** Sets a sprit mainsail and a foresail, as the painting's small craft do, not square sails. */
  readonly spritRigged: boolean;
  /** The painted ships' high hull with castles and transom (docs/plans/ships.md); boats have none. */
  readonly form?: HullForm;
}

export type MastRig = 'square' | 'lateen';

/**
 * A lateen yard crosses its mast at its sling, `sling` of the way from the highest deck under the
 * mast to the masthead, `forward` of its length before the mast, rising aft at `angle` radians.
 */
export interface LateenSpec {
  readonly length: number;
  readonly forward: number;
  readonly angle: number;
  readonly sling: number;
}

export interface MastConfig {
  /** Height above the deck at its foot: the gunwale of a boat, the waist deck of a formed hull. */
  readonly height: number;
  /** Where it stands, as a fraction of the length from the stem. */
  readonly position: number;
  readonly rig?: MastRig;
  readonly lateen?: LateenSpec;
  /** Radius of its round top, in metres; a bare pole has none. */
  readonly top?: number;
  readonly topmast?: boolean;
  /** Shrouds a side in the mid and near tiers; the far tier sets up two. */
  readonly shrouds?: number;
  /** Whether it flies a pennant; every mast does unless this is false. */
  readonly pennant?: boolean;
}

/**
 * A castle deck over part of the hull, `from` and `to` along it from the stem. Its rail stands
 * `rail` metres above the water at its inner end and rises `sheer` metres towards its outer end,
 * the stem for a forecastle, the stern for an aftercastle.
 */
export interface CastleTier {
  readonly from: number;
  readonly to: number;
  readonly rail: number;
  readonly sheer: number;
}

/**
 * The flat stern: its half-width at the rail as a share of the rail's half-breadth amidships, the
 * height of its foot (the tuck) above the water and how far its top stands abaft its foot, in metres.
 */
export interface Transom {
  readonly width: number;
  readonly tuck: number;
  readonly rake: number;
}

export interface HullForm {
  /** Height of the waist rail above the water amidships, in metres. */
  readonly waistRail: number;
  /** The waist deck, as a fraction of the height from keel to rail. */
  readonly deck: number;
  readonly forecastle: CastleTier;
  /** Aftercastle tiers from the lowest; each higher tier stands on the one below. */
  readonly aftcastle: readonly CastleTier[];
  /** How far the stem's foot stands abaft its head, in metres. */
  readonly stemRake: number;
  readonly transom: Transom;
  /** Each wale's centre, as a fraction of the height from keel to rail. */
  readonly wales: readonly number[];
  /** The row of gun ports along the waist: its height as a fraction, its extent along the hull and the spacing in metres. */
  readonly ports: {
    readonly u: number;
    readonly from: number;
    readonly to: number;
    readonly spacing: number;
  };
}

const DEG = Math.PI / 180;

export const SHIP_TEMPLATES: Record<ShipType, ShipTemplate> = {
  /* The great carrack of R3: waist rail, castles and mastheads measured on docs/plans/ships.md 2-3.
     Mast heights keep the mastheads where the painter scale was fitted; the mizzen rises to 0.70 of
     the length above the water (R3 0.80, R34 0.59) and the bonaventure stays below it (woodcut). */
  kraeck: {
    type: 'kraeck',
    length: 30,
    beam: 8,
    masts: [
      { height: 24.48, position: 0.5, top: 0.85, topmast: true, shrouds: 4 },
      { height: 16.729, position: 0.05, top: 0.7, topmast: true, shrouds: 4 },
      /* R3's lateen yards stand 58-63 degrees up (the woodcut's 59), the mizzen's 0.36 of the
         length long and crossing its mast about halfway up (scan 270,4490-470,4690); slung at 0.45
         it passes under the shrouds coming down from the mizzen top on its side. */
      {
        height: 16.185,
        position: 0.71,
        rig: 'lateen',
        lateen: { length: 11.3, forward: 0.39, angle: 60 * DEG, sling: 0.45 },
        top: 0.45,
        shrouds: 3,
      },
      /* R3's bonaventure yard is 0.32 of the length; at 0.26 its low end clears the poop rail. */
      {
        height: 12.274,
        position: 0.88,
        rig: 'lateen',
        lateen: { length: 7.8, forward: 0.35, angle: 60 * DEG, sling: 0.5 },
        shrouds: 2,
        pennant: false,
      },
    ],
    sheerFore: 1.2,
    sheerAft: 1.5,
    keelDepth: 2.5,
    hullExponent: 2.2,
    hasLeeboards: false,
    spritRigged: false,
    form: {
      waistRail: 5.7,
      deck: 0.85,
      forecastle: { from: 0, to: 0.2, rail: 7.9, sheer: 0.9 },
      aftcastle: [
        { from: 0.64, to: 1, rail: 8.8, sheer: 0.4 },
        { from: 0.82, to: 1, rail: 10.9, sheer: 0.9 },
      ],
      stemRake: 2.0,
      transom: { width: 0.62, tuck: 1.2, rake: 1.2 },
      wales: [0.42, 0.58, 0.74],
      ports: { u: 0.66, from: 0.24, to: 0.6, spacing: 2.4 },
    },
  },
  /* R17's ship: a three-master with a lateen mizzen (hulk mizzen 0.62 of its length above the
     water, below the main and fore) and a castle at either end. */
  hulk: {
    type: 'hulk',
    length: 25,
    beam: 7.5,
    masts: [
      { height: 23.532, position: 0.52, top: 0.75, topmast: true, shrouds: 4 },
      { height: 15.691, position: 0.06, top: 0.6, topmast: true, shrouds: 3 },
      /* R17 draws its masts bare: the lateen takes R3's angle, slung 0.6 of the way up so that its
         low end clears the aftercastle rail. */
      {
        height: 11.741,
        position: 0.82,
        rig: 'lateen',
        lateen: { length: 9.0, forward: 0.39, angle: 60 * DEG, sling: 0.6 },
        shrouds: 2,
        pennant: false,
      },
    ],
    sheerFore: 1.0,
    sheerAft: 1.2,
    keelDepth: 2.2,
    hullExponent: 1.6,
    hasLeeboards: false,
    spritRigged: false,
    form: {
      waistRail: 4.25,
      deck: 0.84,
      forecastle: { from: 0, to: 0.16, rail: 7.1, sheer: 0.8 },
      aftcastle: [{ from: 0.7, to: 1, rail: 8.6, sheer: 0.8 }],
      stemRake: 1.6,
      transom: { width: 0.6, tuck: 1.0, rake: 1.0 },
      wales: [0.42, 0.58, 0.74],
      ports: { u: 0.66, from: 0.24, to: 0.6, spacing: 2.4 },
    },
  },
  /* The smaller three-master of R34 and R20 (docs/plans/ships.md 7): low in the waist, a low castle
     at either end, square fore and main with topmasts, a lateen mizzen. R34 steps its mizzen 0.87
     of its length from the stem and heads it 0.59 of its length above the water (section 2); R20
     sets both topsails over its courses. None of the painting's readable ships has two masts. */
  bark: {
    type: 'bark',
    length: 20,
    beam: 6,
    masts: [
      { height: 18.612, position: 0.58, top: 0.6, topmast: true, shrouds: 3 },
      { height: 13.055, position: 0.13, top: 0.5, topmast: true, shrouds: 3 },
      /* The mizzen yard takes the angle of the kraeck's and the hulk's and the bonaventure's
         forward share, 0.33 of the length long: slung halfway up, it clears the aftercastle rail
         below and the mizzen shrouds above, which a yard slung higher or longer would cross. */
      {
        height: 9.122,
        position: 0.87,
        rig: 'lateen',
        lateen: { length: 6.6, forward: 0.35, angle: 60 * DEG, sling: 0.5 },
        shrouds: 2,
        pennant: false,
      },
    ],
    sheerFore: 0.8,
    sheerAft: 1.0,
    keelDepth: 2,
    hullExponent: 2.0,
    hasLeeboards: false,
    spritRigged: false,
    form: {
      waistRail: 3.1,
      deck: 0.8,
      forecastle: { from: 0, to: 0.14, rail: 5.1, sheer: 0.6 },
      aftcastle: [{ from: 0.72, to: 1, rail: 6.5, sheer: 0.6 }],
      stemRake: 1.2,
      transom: { width: 0.6, tuck: 0.8, rake: 0.8 },
      wales: [0.56, 0.74],
      ports: { u: 0.66, from: 0.26, to: 0.52, spacing: 2.2 },
    },
  },
  boeier: {
    type: 'boeier',
    length: 12,
    beam: 4,
    /* The painting's small craft in profile step their mast 0.20-0.27 of the length from the stem,
       with room abaft it for the sprit sail. */
    masts: [{ height: 13, position: 0.22 }],
    sheerFore: 1.2,
    sheerAft: 1,
    keelDepth: 1.2,
    hullExponent: 2.0,
    hasLeeboards: true,
    spritRigged: true,
  },
  rowing: {
    type: 'rowing',
    length: 5,
    beam: 1.6,
    masts: [],
    sheerFore: 0.4,
    sheerAft: 0.3,
    keelDepth: 0.5,
    hullExponent: 2.0,
    hasLeeboards: false,
    spritRigged: false,
  },
  /* The base lighter of the Damrak, fuller than a rowing boat: at 8 x 2.2 m, the lighters
     cover the Damrak of the painter still as darkly as the painted boats (19.8 against 19.9 per
     cent) and reach as far from the west quay (14 against 13 px at the median), where 6.5 m
     lighters fall a fifth short (docs/reference/ship-measure/damrak-count.json boatSize).
     Moored working boats now vary these dimensions individually to reproduce the crop's
     mixed long narrow hulls and small boats, rather than copying this base silhouette. */
  praam: {
    type: 'praam',
    length: 8,
    beam: 2.2,
    masts: [],
    sheerFore: 0.4,
    sheerAft: 0.3,
    keelDepth: 0.6,
    hullExponent: 3.0,
    hasLeeboards: false,
    spritRigged: false,
  },
};

/** The ship types drawn where no measurement of the painting types a ship. */
export type DrawnShip = 'kraeck' | 'hulk' | 'bark';

/**
 * The ships of one deck, dealt shuffled where no measurement of the painting types a ship; small
 * craft are boeiers, unmasted craft rowing boats, and lighters lie only where moored. The
 * painting's four-masters are 4 of the 21 ships whose masts read on the scan, 3 without R16's
 * bonaventure, a bare pole that reads only at 3x (docs/reference/ship-measure/harbour-count.json
 * masts): the kraeck, the one four-master, takes the middle, 3.5 in 21 or 3 in 18. The painted
 * ships measured for their size as three-masters are 4 hulks and 2 barks (berth-sizes.json), so
 * the other 15 split 10 to 5.
 */
export const SHIP_DECK: Readonly<Record<DrawnShip, number>> = { kraeck: 3, hulk: 10, bark: 5 };

/**
 * The one wind over the harbour: the layout heading it blows towards, in radians, measured like
 * ship headings. 145 degrees is the right of the painter frame (121 degrees) turned 24 degrees
 * away from the painter camera, so pennants stream right and rise about 18 degrees in the frame,
 * as the painting's do, and the lee of a ship lying broadside falls on its far side. Turned
 * further, fewer pennants of after masts would stream along the centre line into the next
 * mast's rig, but they would climb more steeply than the painting's.
 */
export const WIND_HEADING = (145 * Math.PI) / 180;

export const HULL_STATIONS = 12;

export function hullHalfBreadth(s: number, beam: number, exponent: number): number {
  return (beam / 2) * (1 - Math.pow(Math.abs(2 * s - 1), exponent));
}

export function sheerHeight(s: number, sheerFore: number, sheerAft: number): number {
  const t = 2 * s - 1;
  if (t < 0) {
    return sheerFore * t * t;
  }
  return sheerAft * t * t;
}

export function deckPolygon(template: ShipTemplate): readonly Vec3[] {
  const { length, beam, hullExponent } = template;
  const points: Vec3[] = [];
  const halfLength = length / 2;

  for (let i = 0; i <= HULL_STATIONS; i++) {
    const s = i / HULL_STATIONS;
    const x = -halfLength + s * length;
    const z = hullHalfBreadth(s, beam, hullExponent);
    const y = sheerHeight(s, template.sheerFore, template.sheerAft);
    points.push([x, y, z]);
  }

  for (let i = HULL_STATIONS; i >= 0; i--) {
    const s = i / HULL_STATIONS;
    const x = -halfLength + s * length;
    const z = -hullHalfBreadth(s, beam, hullExponent);
    const y = sheerHeight(s, template.sheerFore, template.sheerAft);
    points.push([x, y, z]);
  }

  return points;
}

export function hullOBBDimensions(template: ShipTemplate): {
  readonly length: number;
  readonly beam: number;
} {
  return { length: template.length, beam: template.beam };
}
